import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2, ArrowLeft, Loader2, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { trackLead } from '@/lib/fbpixel';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';


const personalEmailDomains = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'aol.com',
  'icloud.com',
  'mail.com',
  'protonmail.com',
  'zoho.com',
];

const demoRequestSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name is too long'),
  workEmail: z
    .string()
    .trim()
    .email('Please enter a valid email')
    .max(255)
    .refine(
      (email) => {
        const domain = email.split('@')[1]?.toLowerCase();
        return !personalEmailDomains.includes(domain);
      },
      'Please use your work email address'
    ),
  companyName: z
    .string()
    .trim()
    .min(1, 'Company name is required')
    .max(200),
  companySize: z.enum(['1-10', '11-49', '50-249', '250-999', '1000+'], {
    required_error: 'Please select your company size',
  }),
  jobTitle: z.string().trim().max(100).optional(),
  phoneNumber: z.string().trim().max(30).optional(),
  message: z.string().trim().max(500).optional(),
});

type DemoRequestFormData = z.infer<typeof demoRequestSchema>;

interface DemoRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const companySizeOptions = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-49', label: '11-49 employees' },
  { value: '50-249', label: '50-249 employees' },
  { value: '250-999', label: '250-999 employees' },
  { value: '1000+', label: '1000+ employees' },
];

export function DemoRequestDialog({ open, onOpenChange }: DemoRequestDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [submittedName, setSubmittedName] = useState('');
  const { toast } = useToast();

  // Load Tidycal script when calendar view is shown
  useEffect(() => {
    if (showCalendar) {
      const existingScript = document.querySelector('script[src*="tidycal"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://asset-tidycal.b-cdn.net/js/embed.js';
        script.async = true;
        document.body.appendChild(script);
      }
    }
  }, [showCalendar]);

  const form = useForm<DemoRequestFormData>({
    resolver: zodResolver(demoRequestSchema),
    defaultValues: {
      fullName: '',
      workEmail: '',
      companyName: '',
      companySize: undefined,
      jobTitle: '',
      phoneNumber: '',
      message: '',
    },
  });

  const handleSubmit = async (data: DemoRequestFormData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('submit-demo-request', {
        body: data,
      });

      if (error) throw error;

      setSubmittedName(data.fullName.split(' ')[0]);
      trackLead('main_demo_form', {
        email: data.workEmail,
        phone: data.phoneNumber,
        fullName: data.fullName,
      });
      setIsSuccess(true);
      form.reset();
    } catch (error) {
      console.error('Demo request error:', error);
      toast({
        title: 'Something went wrong',
        description: 'Please try again or contact us directly.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset states after close animation
    setTimeout(() => {
      setIsSuccess(false);
      setShowCalendar(false);
      setSubmittedName('');
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={`${showCalendar ? 'sm:max-w-4xl w-[95vw]' : 'sm:max-w-md'} max-h-[90vh] flex flex-col`}>
        {showCalendar ? (
          // Calendar View
          <div className="flex flex-col h-full min-h-0">
            <div className="flex items-center gap-2 mb-2 flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCalendar(false)}
                className="p-0 h-auto hover:bg-transparent"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            </div>
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="text-xl">Book Your Demo</DialogTitle>
              <p className="text-sm text-muted-foreground">
                Select a time that works best for you
              </p>
            </DialogHeader>
            <div className="flex-1 min-h-0 mt-4 overflow-y-auto">
              <div 
                className="tidycal-embed min-h-[600px]" 
                data-path="beatview/hiring-solution"
              />
            </div>
          </div>
        ) : isSuccess ? (
          // Success + Book Now View
          <div className="py-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <CheckCircle2 className="h-6 w-6 text-primary" />
            </div>
            <DialogHeader>
              <DialogTitle className="text-xl text-center">Demo Request Received</DialogTitle>
            </DialogHeader>
            <p className="mt-3 text-muted-foreground">
              Thanks, {submittedName}! Want to skip the wait?
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <Button onClick={() => setShowCalendar(true)} size="lg">
                <Calendar className="mr-2 h-4 w-4" />
                Choose Your Demo Time
              </Button>
              <p className="text-sm text-muted-foreground">
                or we'll reach out within 24 hours
              </p>
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="text-xl">See Beatview in Action</DialogTitle>
              <p className="text-sm text-muted-foreground">
                Get a personalized demo for your team
              </p>
            </DialogHeader>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col flex-1 min-h-0 overflow-hidden">
                <div className="relative flex-1 min-h-0 overflow-y-auto pr-2">
                  <div className="space-y-4 pb-2">
                      <FormField
                        control={form.control}
                        name="fullName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Full Name *</FormLabel>
                            <FormControl>
                              <Input placeholder="John Smith" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="workEmail"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Work Email *</FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                placeholder="john@company.com"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="companyName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company Name *</FormLabel>
                            <FormControl>
                              <Input placeholder="Acme Inc." {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="companySize"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company Size *</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select company size" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {companySizeOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="jobTitle"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Job Title (optional)</FormLabel>
                            <FormControl>
                              <Input placeholder="Head of Talent" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="phoneNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone Number (optional)</FormLabel>
                            <FormControl>
                              <Input placeholder="+1 (555) 000-0000" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Message (optional)</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Tell us what you'd like to see in the demo..."
                                className="resize-none"
                                rows={3}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                  </div>
                  {/* Scroll indicator gradient */}
                  <div className="absolute bottom-0 left-0 right-2 h-6 bg-gradient-to-t from-background to-transparent pointer-events-none" />
                </div>

                <div className="flex-shrink-0 pt-4 mt-2 border-t space-y-3">
                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Request Demo'
                    )}
                  </Button>

                  <p className="text-center text-xs text-muted-foreground">
                    By submitting, you agree to our{' '}
                    <a href="/privacy-policy" className="underline hover:text-foreground">
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a href="/terms-of-service" className="underline hover:text-foreground">
                      Terms
                    </a>
                  </p>
                </div>
              </form>
            </Form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
